import React, { useState, useMemo } from 'react'
import SelectInput from 'components/core/select-input'
import TextInput from 'components/core/text-input'
import { useFormik } from 'formik'
import { classNames, tabs } from 'utils'
import { useMutation } from '@apollo/client'
import { generatePaymentReport } from 'graphql/mutations/reports'
import wrapClick from 'utils/wrap-click'
import { Link } from 'react-location'
import useUrlState from 'utils/use-url-state'
import _ from 'lodash'
import { GeneratePaymentReport, GeneratePaymentReportVariables } from 'graphql/mutations/__generated__/GeneratePaymentReport'
import { useTitle } from 'react-use'
import numeral from 'numeral'

type Props = {}

const PAGE_SIZE = 10

const PaymentsReportsTabs = (props: Props) => {
    useTitle("Kava | Payments Reports")
    const [orderStatus, setOrderStatus] = useUrlState("brokerTab");
    const [initiate, { loading, data }] = useMutation<GeneratePaymentReport, GeneratePaymentReportVariables>(generatePaymentReport)
    const [currentPage, setCurrentPage] = useState(1);

    // Reset page when tab changes or data changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [orderStatus, data]);

    const form = useFormik({
        initialValues: {
            from: '',
            to: '',
            brokers: []
        },
        onSubmit: (values) => {
            initiate({
                variables: {
                    args: {
                        brokers: values.brokers,
                        from: values.from,
                        to: values.to
                    }
                }
            }).then(res => {
                setOrderStatus(res.data?.generatePaymentReport?.[0]?.name || undefined)
            }).catch(err => {
                console.log(err)
            })
        }
    })

    // Get the current tab's data
    const currentTabData = useMemo(() => {
        return _.get(
            _.find(data?.generatePaymentReport, el => el?.name && el?.name === orderStatus),
            "data",
            []
        );
    }, [data, orderStatus]);

    // Pagination logic
    const totalRows = currentTabData.length;
    const totalPages = Math.ceil(totalRows / PAGE_SIZE);

    const paginatedData = useMemo(() => {
        const startIdx = (currentPage - 1) * PAGE_SIZE;
        return currentTabData.slice(startIdx, startIdx + PAGE_SIZE);
    }, [currentTabData, currentPage]);

    return (
        <div className='w-full'>
            <div className="rounded-lg  bg-white px-4 shadow w-full">
                <div className='grid gap-4  py-7 grid-cols-1 md:grid-cols-2'>
                    <div className='col-span-2'>
                        <SelectInput label='Broker(s)' multiple id={'brokers'} options={[
                            ...tabs.map(tab => ({ label: tab.name, value: tab.href }))
                        ]} {...form} />
                    </div>
                    <div>
                        <TextInput label='From' id={'from'} type='date' {...form} />
                    </div>
                    <div>
                        <TextInput label='To' id={'to'} type='date' {...form} />
                    </div>
                </div>
                <div className='pb-6'>
                    <button disabled={loading} onClick={wrapClick(form.handleSubmit)} className='bg-green-500 disabled:bg-gray-500 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md'>{loading ? "Please wait ..." : "Generate Report"}</button>
                </div>
            </div>

            <div className="block">
                <div className="border-b border-gray-200 ">
                    <nav className="-mb-px flex space-x-8 items-center" aria-label="Tabs">
                        {data?.generatePaymentReport?.map((_orderStatus) => (
                            <Link
                                key={_orderStatus?.name}
                                search={(old) => ({
                                    ...old,
                                    brokerTab: _orderStatus?.name,
                                })}
                                className={classNames(
                                    orderStatus === _orderStatus?.name
                                        ? "border-green-500 text-green-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200",
                                    "whitespace-nowrap flex py-4 px-1 border-b-2 font-medium text-sm"
                                )}
                                aria-current={
                                    orderStatus === _orderStatus?.name ? "page" : undefined
                                }
                            >
                                {_orderStatus?.name}
                            </Link>
                        ))}
                    </nav>
                </div>
            </div>
            {data?.generatePaymentReport && (
                <div className="flex flex-1 overflow-y-auto w-full">
                    <div className="flex-1 mx-auto min-w-0   sm:py-6 overflow-hidden overflow-y-auto light flex">
                        <div className="flow-root w-full">
                            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                        <table className="min-w-full divide-y divide-gray-300">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                        Policy Number
                                                    </th>
                                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Cedant
                                                    </th>
                                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Insured
                                                    </th>
                                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Payment Type
                                                    </th>
                                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Payment Amount
                                                    </th>
                                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Period From
                                                    </th>
                                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Period To
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                {paginatedData.map((person: any, idx) => (
                                                    <tr key={idx}>
                                                        <td className="py-4 text-sm max-w-0 w-auto truncate font-medium text-gray-900 sm:pl-6 ">
                                                            {person?.policy_number}
                                                        </td>
                                                        <td className=" max-w-0 w-auto truncate px-3 py-4 text-sm text-gray-500">{person.cedant}</td>
                                                        <td className=" max-w-0 w-auto truncate px-3 py-4 text-sm text-gray-500">{person.insured}</td>
                                                        <td className=" max-w-0 w-auto truncate px-3 py-4 text-sm text-gray-500">{person.payment_type}</td>
                                                        <td className=" max-w-0 w-auto truncate px-3 py-4 text-sm text-gray-500">{person?.currency} {numeral(person.payment_amount).format("0,0.00")}</td>
                                                        <td className=" max-w-0 w-auto truncate px-3 py-4 text-sm text-gray-500">{person.period_from}</td>
                                                        <td className=" max-w-0 w-auto truncate px-3 py-4 text-sm text-gray-500">{person.period_to}</td>
                                                    </tr>
                                                ))}
                                                {paginatedData.length === 0 && (
                                                    <tr>
                                                        <td colSpan={7} className="text-center py-4 text-gray-500">
                                                            No data available.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                        {/* Pagination Controls */}
                                        {totalPages > 1 && (
                                            <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                                                <div className="flex-1 flex justify-between sm:justify-end">
                                                    <button
                                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                        disabled={currentPage === 1}
                                                        className="relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Previous
                                                    </button>
                                                    <span className="mx-2 flex items-center text-sm text-gray-700">
                                                        Page {currentPage} of {totalPages}
                                                    </span>
                                                    <button
                                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                        disabled={currentPage === totalPages}
                                                        className="relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default PaymentsReportsTabs